<template>
  <div>
    <!-- Rename Folder Modal -->
    <b-modal
      id="rename-folder-modal"
      title="Rename Folder"
      centered
      @ok="renameFolder"
      @cancel="cancelRenameFolder"
      @hidden="cancelRenameFolder"
    >
      <b-form-group label="Folder name">
        <b-form-input
          v-model="renamingFolderName"
          placeholder="Enter folder name"
          @keyup.enter="renameFolder"
        />
      </b-form-group>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="outline-secondary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!renamingFolderName.trim()"
          @click="ok()"
        >
          Rename
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="brief-folder-rename-modal"
      title="Rename linked brief folder"
      centered
      @hidden="cancelBriefFolderRename"
    >
      <p class="mb-2">
        This folder is linked to the brief
        <strong>{{ briefRenameBriefTitle }}</strong>.
        Rename the brief too?
      </p>
      <p class="text-muted small mb-0">
        Choosing &ldquo;Just the folder&rdquo; stops automatic syncing. The brief will get a new folder the next time assets are added.
      </p>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="cancelBriefFolderRename"
        >
          Cancel
        </b-button>
        <b-button
          variant="outline-primary"
          @click="confirmBriefFolderRename('folder_only')"
        >
          Just the folder
        </b-button>
        <b-button
          variant="primary"
          @click="confirmBriefFolderRename('both')"
        >
          Rename both
        </b-button>
      </template>
    </b-modal>

    <div class="folder-list-container">
      <div class="folder-sticky-header">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="search-container flex-grow-1 mr-3">
            <div class="search-input-wrapper">
              <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                placeholder="Search in this folder..."
                @keyup.enter="performSearch"
              >
              <svg
                width="13"
                height="13"
                class="search-icon"
              >
                <use href="/img/icons/sprite.svg#search-big" />
              </svg>
              <button
                v-if="searchQuery"
                class="clear-search-btn"
                @click="clearSearch"
              >
                <i class="cil-x" />
              </button>
            </div>
          </div>
          <div>
            <b-btn
              class="c-top-bar__reset-button c-top-bar__settings-button c-top-bar__search--button ml-2"
              title="New folder"
              @click="createFolder"
            >
              <FolderPlusIcon
                class="c-top-bar__search--new-folder-button"
                :width="12"
                :height="12"
              />
            </b-btn>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <BreadcrumbItem
            :breadcrumbs="breadcrumbs"
            @goTo="goToFolder"
          />

          <div
            v-if="allowSelectAll && allowMultipleSelection"
            class="d-flex align-items-center"
          >
            <b-btn
              v-if="!hasSelectedItems"
              class="select-all-btn"
              @click="selectAllItems"
            >
              Select All
            </b-btn>

            <b-btn
              v-else
              class="reset-all-btn"
              @click="clearSelectedItems"
            >
              Clear Selections
            </b-btn>
          </div>
        </div>
      </div>

      <div
        v-if="items.length || isListLoading || creatingFolder"
        class="folder-scroll-area"
      >
        <template v-if="isListLoading || creatingFolder">
          <div class="folders-grid">
            <div
              v-for="n in pageSize"
              :key="n"
              class="w-100 h-100"
              style="min-height: 167px;"
            >
              <b-skeleton
                width="100%"
                height="88%"
              />
              <b-skeleton />
            </div>
          </div>
        </template>

        <template v-else>
          <b-alert
            v-if="isSearchActive && items.length && wasEnterPressed"
            show
            variant="info"
            class="mb-3"
          >
            Found {{ totalCount }} result{{ totalCount > 1 ? 's' : '' }} for "{{ searchQuery }}"
          </b-alert>
        
          <b-alert
            v-if="isSearchActive && !items.length && !isListLoading"
            show
            variant="warning"
            class="mb-3"
          >
            No results found for "{{ searchQuery }}"
          </b-alert>

          <div class="section-container">
            <ItemGrid
              v-if="folderItems.length"
              class="folder-items folders-grid"
              :items="folderItems"
              :is-selected="isSelected"
              :columns="4"
              :gap="16"
              :item-props-for="(item) => ({ showEditButton: canRenameFolder(item) })"
              @item-click="onSelectedItem"
              @item-dblclick="dblHandler"
              @edit="startRenamingFolder"
            />
            <b-alert
              v-else-if="!isSearchActive"
              show
              variant="info"
              class="mb-4"
            >
              No folders available
            </b-alert>
          </div>

          <div class="section-container">
            <ItemGrid
              v-if="otherItems.length"
              class="folders-grid"
              :items="otherItems"
              :is-selected="isSelected"
              :columns="4"
              :gap="16"
              :item-props="{ isAvailableOverlay: availableOverlay }"
              @item-click="onSelectedItem"
              @item-dblclick="fileDoubleClickHandler"
            />
          </div>
        </template>
      </div>

      <div
        v-else
        class="folder-scroll-area"
      >
        <b-alert
          show
          variant="warning"
        >
          Folder is empty
        </b-alert>
      </div>

      <div class="folder-sticky-footer">
        <div
          v-if="selectedItems.length"
          class="selection-summary"
        >
          <span class="font-weight-bold">{{ selectedItems.length }} item{{ selectedItems.length > 1 ? 's' : '' }} selected</span>
        </div>
        <div class="d-flex justify-content-center align-items-center item-pagination">
          <ItemPagination
            v-model="currentPage"
            :per-page="pageSize"
            :total-rows="totalCount"
            :disabled="isListLoading"
            @change="onChangePage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ItemPagination from "@frontend/components/modules/file-browser/browser/ItemPagination.vue";
/* eslint vue/enforce-style-attribute: 0 */
import { createNamespacedHelpers } from "vuex";
import ItemGrid from "@frontend/components/modules/file-browser/browser/ItemGrid.vue";
import { TABS, TAB_SOURCE_MAP, MODALS, VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE } from "./constants";
import { FILE_TYPE, TYPE, SOURCE } from "../../../constants/file-image-types";
import fetchListDTO, {fetchListDataBasedActionCreator} from "./dto/fetch-list-dto";
import FileBrowser from "../../../services/api/file-browser";
import BreadcrumbItem from "../../common/atoms/BreadcrumbItem.vue";
import {IMAGE_TYPE_PRODUCT} from "@frontend/constants/file-upload-images-types";
import {createModal} from "./modals/action-creater";
import { isBriefLinkedFolder } from "./helpers";
import {FolderPlus as FolderPlusIcon} from "lucide-vue";

const DEFAULT_FOLDER_NAME = 'New Folder';

const {
  mapGetters: mapGettersFileBrowser,
  mapActions: mapActionsFileBrowser,
  mapMutations: mapMutationsFileBrowser,
} = createNamespacedHelpers('fileBrowser');

const {
  mapGetters: mapGettersFileUploadImages
} = createNamespacedHelpers('fileUploadImages');

export default {
  components: {
    ItemPagination,BreadcrumbItem, ItemGrid, FolderPlusIcon },
  props: {
    isOnlyFolder: {
      type: Boolean,
      default: false,
    },
    pageSize: {
      type: Number,
      default: 19,
    },
    allowMultipleSelection: {
      type: Boolean,
      default: true,
    },
    allowSelectAll: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      currentPage: 1,
      selectedItems: [],
      breadcrumbs: [],
      currentFolderId: null,
      searchQuery: '',
      wasEnterPressed: false,
      newFolderName: '',
      isLoading: false,
      creatingFolder: false,
      renamingFolderId: null,
      renamingFolderName: '',
      renamingFolderItem: null,
      briefRenameBriefTitle: '',
      pendingBriefRenameName: '',
    }
  },
  computed: {
    ...mapGettersFileBrowser([
      'items',
      'totalCount',
      'isListLoading',
    ]),
    ...mapGettersFileUploadImages([
      'getSelectedImageType',
      'getContentType',
      'getTab',
    ]),
    isSelected() {
      const selectedIds = this.selectedItems.map(({ id }) => id);
      return item => selectedIds.includes(item.id);
    },
    availableOverlay() {
      return [TABS.PRODUCTS, TABS.STOCK_IMAGES, TABS.FILES].includes(this.$route?.params?.tab);
    },
    folderItems() {
      return this.items.filter(item => item.type === FILE_TYPE.FOLDER);
    },
    otherItems() {
      return this.items.filter(item => item.type !== FILE_TYPE.FOLDER);
    },
    isSearchActive() {
      return !!this.searchQuery.trim();
    },
    hasSelectedItems() {
      return this.selectedItems.length > 0;
    }
  },
  watch: {
    getTab(tab) {
      if (tab !== TABS.SALSIFY_ASSETS && tab !== TABS.SALSIFY_PRODUCTS) {
        this.fetchFolderList();
      }
    }
  },
  mounted() {
    this.setPageSize(this.pageSize);
    this.fetchFolderList();
  },
  methods: {
    ...mapActionsFileBrowser(['fetchList', 'setShowOnlyFolders', 'editFile', 'clearSelection', 'addFolder']),
    ...mapMutationsFileBrowser({
      setPageSize: 'SET_PAGE_SIZE',
      SET_MODAL: 'SET_MODAL'
    }),
    fetchFolderList() {
      this.fetchList(fetchListDataBasedActionCreator(
        this.getTab,
        this.currentFolderId ? this.currentFolderId.toString() : null,
        this.currentPage,
        '',
      ));
    },
    performSearch() {
      if (this.searchQuery.trim()) {
        this.currentPage = 1;
        this.wasEnterPressed = true;
        this.fetchSearchResults();
      } else {
        this.clearSearch();
      }
    },
    clearSearch() {
        this.searchQuery = '';
        this.wasEnterPressed = false;
        this.fetchFolderList();
    },
    fetchSearchResults() {
      this.fetchList(fetchListDataBasedActionCreator(
        this.getTab,
        this.currentFolderId ? this.currentFolderId.toString() : null,
        this.currentPage,
        this.searchQuery,
      ));
    },
    async dblHandler(item) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      this.currentPage = 1;
      this.setSelectedItems();
      this.currentFolderId = item.id;

      if (item.is_root === true) {
        this.breadcrumbs = [];
      }

      if (item.is_root === false) {
        this.breadcrumbs.pop();
      }

      if (item.created_at) {
        this.breadcrumbs.push({
          id: item.id,
          text: item.name,
        });
      }

      this.searchQuery = currentSearchQuery;
      
      if (this.searchQuery.trim()) {
        await this.fetchSearchResults();
      } else {
        await this.fetchFolderList();
      }
    },
    fileDoubleClickHandler(item) {
      if (item.type === FILE_TYPE.FOLDER) return;
      
      if (![VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE].some(types => types.includes(item.type))) {
        return;
      }
      
      return this.SET_MODAL(createModal(MODALS.PREVIEW_IMAGE, item));
    },
    async goToFolder(folderId, breadcrumbIndex) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      breadcrumbIndex = folderId?.index;
      folderId = folderId?.id;
      if (this.currentFolderId === folderId) return;

      this.currentPage = 1;
      this.setSelectedItems();
      this.currentFolderId = folderId;
      this.breadcrumbs.splice(breadcrumbIndex);

      this.searchQuery = currentSearchQuery;
      
      if (this.searchQuery.trim()) {
        await this.fetchSearchResults();
      } else {
        await this.fetchFolderList();
      }
    },
    onChangePage(page) {
      this.currentPage = page;
      if (this.isSearchActive) {
        this.fetchSearchResults();
      } else {
        this.fetchFolderList();
      }
    },
    setSelectedItems(selectedItems = []) {
      this.selectedItems = selectedItems;
      this.$emit('onSelectedItems', selectedItems);
    },
    onSelectedItem(item) {
      if (!item.created_at) return;

      const isSelected = this.isSelected(item);

      if (isSelected) {
        this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id);
      } else {
        if (!this.allowMultipleSelection) {
          this.selectedItems = [];
        }
        this.selectedItems.push(item);
      }

      this.setSelectedItems(this.selectedItems);
    },
    async selectAllItems() {
      if (this.totalCount <= this.pageSize) {
        this.selectedItems = [...this.otherItems];
        this.setSelectedItems(this.selectedItems);
        return;
      }

      try {
        let source = TAB_SOURCE_MAP[this.getTab] || SOURCE.FOLDER;
        if (this.currentFolderId) {
          source = `${SOURCE.FOLDER_CONTENT_RESOLVE_POINT}&folderId=${this.currentFolderId}`;
        }
        if (this.searchQuery) {
          source = source.includes('?') ? `${source}&searchKey=${this.searchQuery}` : `${source}?searchKey=${this.searchQuery}`;
        }

        const { data } = await FileBrowser.listBySource(source, {
          pageSize: this.totalCount,
          folder_id: this.currentFolderId,
        });

        const allFiles = data.items.filter(item => item.type !== FILE_TYPE.FOLDER);
        this.selectedItems = allFiles;
        this.setSelectedItems(this.selectedItems);
      } catch (error) {
        console.error('Error fetching all items:', error);
        this.selectedItems = [...this.otherItems];
        this.setSelectedItems(this.selectedItems);
      }
    },
    clearSelectedItems() {
      this.selectedItems = [];
      this.setSelectedItems([]);
    },
    async createFolder() {
      try {
        this.creatingFolder = true;
        let baseName = DEFAULT_FOLDER_NAME;
        let folderName = baseName;
        let counter = 1;
        let folderExists = true;
        while (folderExists) {
          const response = await axios.post('/file/check_folder_name_exists', {
            name: folderName,
            parent_id: this.currentFolderId || null
          });

          if (response.data.exists) {
            folderName = `${baseName} (${counter})`;
            counter++;
          } else {
            folderExists = false;
          }
        }

        this.newFolderName = folderName;
        const responseFolder = await axios.post('/file/create_folder_by_name', {
          name: folderName,
          parent_id: this.currentFolderId || null,
        });
        this.folderId = responseFolder.data.id;

        // inside a folder every sub-folder is listed on every page, so the new one is already here
        if (!this.currentFolderId) {
          this.currentPage = Math.ceil((this.totalCount + 1) / this.pageSize);
        }

        await this.fetchFolderList();
        toastr.success(`Folder "${folderName}" created`);
      } catch (error) {
        console.error('Error creating folder:', error);
        toastr.error('Failed to create folder');
      } finally {
        this.creatingFolder = false;
      }
    },
    canRenameFolder(item) {
      return item && item.created_at && item.type === FILE_TYPE.FOLDER;
    },
    startRenamingFolder(item) {
      this.renamingFolderId = item.id;
      this.renamingFolderName = item.name;
      this.renamingFolderItem = item;
      this.$bvModal.show('rename-folder-modal');
    },
    async renameFolder() {
      if (!this.renamingFolderName.trim()) {
        toastr.error('Folder name cannot be empty');
        return;
      }

      const nextName = this.renamingFolderName.trim();
      const item = this.renamingFolderItem
        || this.folderItems.find((folder) => folder.id === this.renamingFolderId);

      if (
        isBriefLinkedFolder(item)
        && nextName !== item.name
      ) {
        this.pendingBriefRenameName = nextName;
        this.briefRenameBriefTitle = item.brief_title || item.name;
        this.$bvModal.hide('rename-folder-modal');
        this.$bvModal.show('brief-folder-rename-modal');
        return;
      }

      await this.performFolderRename(nextName);
    },
    async performFolderRename(name, briefRenameAction = null) {
      try {
        await this.editFile({
          id: this.renamingFolderId,
          name,
          item: { type: FILE_TYPE.FOLDER },
          brief_rename_action: briefRenameAction,
        });
        await this.fetchFolderList();
        this.resetRenameState();
      } catch (error) {
        console.error('Error renaming folder:', error);
      }
    },
    async confirmBriefFolderRename(action) {
      await this.performFolderRename(this.pendingBriefRenameName, action);
      this.$bvModal.hide('brief-folder-rename-modal');
    },
    cancelBriefFolderRename() {
      this.$bvModal.hide('brief-folder-rename-modal');
      this.pendingBriefRenameName = '';
      this.briefRenameBriefTitle = '';
    },
    cancelRenameFolder() {
      this.$bvModal.hide('rename-folder-modal');
      this.resetRenameState();
    },
    resetRenameState() {
      this.renamingFolderId = null;
      this.renamingFolderName = '';
      this.renamingFolderItem = null;
      this.pendingBriefRenameName = '';
      this.briefRenameBriefTitle = '';
    },
  }
}
</script>

<style lang="scss">
.folder-list-container {
  user-select: none;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none;    /* Firefox */
  -ms-user-select: none;     /* IE10+ */
  .breadcrumb {
    background: unset;
    border-radius: unset;
    border: unset;
  }

  .section-container {
    margin-bottom: 2rem;
  }

  .section-title {
    margin-bottom: 1rem;
    font-weight: 600;
    border-bottom: 1px solid #e9e9e9;
    padding-bottom: 0.5rem;
  }

  .folders-grid {
    grid-template-rows: auto;
    justify-items: center;
    margin-bottom: 1rem;
  }
  
  .search-container {
    margin-bottom: 0;
  }
  
  .search-input-wrapper {
    position: relative;
    width: 100%;
  }
  
  .search-input {
    width: 100%;
    height: 35px;
    background: white;
    border: 1px solid #b1b1b1;
    border-radius: 12px;
    padding: 0 35px 0 10px;
    outline: 0;
    font-family: "Nunito", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #464646;
  }
  
  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
  
  .clear-search-btn {
    position: absolute;
    right: 35px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #727272;
    padding: 0;
    font-size: 14px;
    
    &:hover {
      color: #333;
    }
  }
  
  .selection-summary {
    font-size: 14px;
    color: #464646;
  }

  .select-all-btn, .reset-all-btn {
    color: #727272;
    font-weight: 700;
    font-size: 14px;
    padding: 0;
    border: unset!important;
    background-color: unset;

    &:hover {
      opacity: 0.75;
    }

    &:focus,
    &:active {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
      background-color: unset !important;
      color: #727272 !important;
    }
  }
}
</style>
